import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, Radio, Row, Select, Space, Spin, Typography, message, Modal } from 'antd';
import dayjs from 'dayjs';
import { orderAuditApi } from '../../../services/process/order-audit';
import { orderApi } from '../../../services/process/order';
import { OrderAuditContactEntity, OrderAuditEntity, pickDurationRange } from '../../../types/process/order-audit';
import { MergedOrder } from '../../../types/process/order';
import OrderBlockShowSimple from '../components/OrderBlockShowSimple';
import ResourceUploader from '../../../components/Business/ResourceUploader';

interface TeamAuditConfig {
  id: number;
  fieldName: string;
  displayName?: string;
  fieldType: 'text' | 'number' | 'radio' | 'select' | 'upload';
  description: string;
  isRequired: boolean;
  fieldMeta: any;
}
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../../components/Layout/AuthContext';
import { OperationAction } from '../../../types/process/operation-log';
import SuspendPendingModal from './modals/SuspendPendingModal';
import { AuditInterruptModal } from './modals/AuditInterruptModal';
import ProfitEstimationModal from '../components/modals/ProfitEstimationModal';
import DecisionModal from '../decision/components/DecisionModal';

const { TextArea } = Input;
const { Text } = Typography;

const readOnlyFormStyle = `
  .readonly-form .ant-input-disabled,
  .readonly-form .ant-input-number-disabled,
  .readonly-form .ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
    color: rgba(0, 0, 0, 0.85);
    font-weight: bold;
    background-color: #f5f5f5;
  }
  .readonly-form .ant-radio-wrapper-disabled.ant-radio-wrapper-checked span {
    color: rgba(0, 0, 0, 0.85);
    font-weight: bold;
  }
`;

interface AuditParams {
  audit: Partial<OrderAuditEntity>;
  auditContacts: OrderAuditContactEntity[];
  auditOther: {
    auditStartTime?: string;
    auditEndTime?: string;
    [key: string]: any;
  };
  teamExtraAuditInfo?: any;
}

const AUDIT_OCCUPATION_OPTIONS = [
  { label: '学生', value: '学生' },
  { label: '个体', value: '个体' },
  { label: '前台', value: '前台' },
  { label: '服务员', value: '服务员' },
  { label: '运营', value: '运营' },
  { label: '客服', value: '客服' },
  { label: '外卖员', value: '外卖员' },
  { label: '快递员', value: '快递员' },
  { label: '销售', value: '销售' },
  { label: '工厂工人', value: '工厂工人' },
  { label: '其他', value: '其他' },
] as const;

const AUDIT_OCCUPATION_VALUES = new Set(AUDIT_OCCUPATION_OPTIONS.map((i) => i.value));
const AUDIT_OCCUPATION_STUDENT = '学生';
const AUDIT_OCCUPATION_OTHER = '其他';

const AUDIT_MARITAL_STATUS_VALUE_MAP = {
  未婚: 1,
  已婚: 2,
  离异: 3,
  丧偶: 4,
} as const;

const AUDIT_CREDIT_REPORT_STATUS_VALUE_MAP = {
  良好: 1,
  一般: 2,
  较差: 3,
  无信用记录: 4,
} as const;

const AUDIT_RENTAL_HISTORY_STATUS_VALUE_MAP = {
  良好: 1,
  一般: 2,
  较差: 3,
  无租赁记录: 4,
} as const;

const AUDIT_CONCLUSION_STATUS_VALUE_MAP = {
  通过: 3,
  拒绝: 4,
} as const;

const normalizeChinaMobilePhone = (input: string): string => {
  const digits = input.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 13 && digits.startsWith('86')) return digits.slice(2);
  return digits;
};

const isValidChinaMobilePhone = (digits: string): boolean => /^1[3-9]\d{9}$/.test(digits);

const coerceEnumNumber = (value: unknown, map: Record<string, number>): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const trimmed = value.replace(/\s*\(.*\)\s*/g, '').trim();
    if (!trimmed) return undefined;
    if (/^\d+$/.test(trimmed)) return Number(trimmed);
    const mapped = map[trimmed];
    if (typeof mapped === 'number') return mapped;
  }
  return undefined;
};

/**
 * 将接口返回的审核数据转换为表单使用的文字枚举值
 */
const normalizeAuditEntityForForm = (audit: Partial<OrderAuditEntity>): Partial<OrderAuditEntity> => {
  const next: Partial<OrderAuditEntity> = { ...(audit || {}) };

  next.maritalStatus = coerceEnumNumber(next.maritalStatus, AUDIT_MARITAL_STATUS_VALUE_MAP);
  next.creditReportStatus = coerceEnumNumber(next.creditReportStatus, AUDIT_CREDIT_REPORT_STATUS_VALUE_MAP);
  next.rentalHistoryStatus = coerceEnumNumber(next.rentalHistoryStatus, AUDIT_RENTAL_HISTORY_STATUS_VALUE_MAP);
  next.status = coerceEnumNumber(next.status, AUDIT_CONCLUSION_STATUS_VALUE_MAP);

  if (next.occupation === 'student') {
    next.occupation = AUDIT_OCCUPATION_STUDENT;
  }

  if (typeof next.occupation === 'string') {
    const occupation = next.occupation.trim();
    if (!occupation) {
      next.occupation = undefined;
    } else if (!AUDIT_OCCUPATION_VALUES.has(occupation as any)) {
      next.occupation = AUDIT_OCCUPATION_OTHER;
      if (!next.occupationOther) {
        next.occupationOther = occupation;
      }
    } else {
      next.occupation = occupation;
    }
  }

  return next;
};

/**
 * 将表单中的文字枚举值转换为接口使用的数字枚举值
 */
const buildAuditEntityForSubmit = (audit: Partial<OrderAuditEntity>): Partial<OrderAuditEntity> => {
  const next: Partial<OrderAuditEntity> = { ...(audit || {}) };

  if (next.occupation === AUDIT_OCCUPATION_OTHER) {
    const occupationOther = typeof next.occupationOther === 'string' ? next.occupationOther.trim() : '';
    if (occupationOther) {
      next.occupation = occupationOther;
    }
    next.occupationOther = undefined;
  } else {
    next.occupationOther = undefined;
  }

  return next;
};

const OrderAuditPage: React.FC = () => {
  const { orderNo } = useParams<{ orderNo: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewMode = new URLSearchParams(location.search).get('mode') === 'view';
  const { staff } = useAuth();
  const currentUserId = staff?.id || 0;
  const originalTitleRef = useRef<string>('');
  const [form] = Form.useForm<AuditParams>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderDetail, setOrderDetail] = useState<MergedOrder | null>(null);
  const [auditConfig, setAuditConfig] = useState<{ default: TeamAuditConfig[], add: TeamAuditConfig[] }>({ default: [], add: [] });
  const [startTime, setStartTime] = useState(dayjs().format('YYYY-MM-DD HH:mm:ss'));
  const [finishModalVisible, setFinishModalVisible] = useState(false);
  const [isoStartTime, setIsoStartTime] = useState(dayjs().toISOString());

  useEffect(() => {
    if (!originalTitleRef.current) {
      originalTitleRef.current = document.title || '';
    }
    return () => {
      document.title = originalTitleRef.current || '';
    };
  }, []);

  useEffect(() => {
    const userName = orderDetail?.orderData?.userAuthName || orderDetail?.order?.userName || '';
    const nextTitle = userName
      ? `${userName} - 订单审核`
      : orderNo
        ? `${orderNo} - 订单审核`
        : '订单审核';
    document.title = nextTitle;
  }, [orderDetail?.order?.userName, orderDetail?.orderData?.userAuthName, orderNo]);

  useEffect(() => {
    if (orderNo) {
      const now = dayjs();
      setStartTime(now.format('YYYY-MM-DD HH:mm:ss'));
      setIsoStartTime(now.toISOString());
      initPage();
    }
  }, [orderNo]);


  const buildValidationSummary = (error: any): { count: number; labels: string[]; modules: string[] } => {
    const errorFields = Array.isArray(error?.errorFields) ? error.errorFields : [];
    const occupation = form.getFieldValue(['audit', 'occupation']);

    const getModuleName = (namePath: (string | number)[]) => {
      const root = namePath?.[0];
      const key = namePath?.[1];
      if (root === 'auditContacts') return '社会关系';
      if (root !== 'audit') return '表单';
      if (typeof key !== 'string') return '表单';
      if (key.startsWith('suspected')) return '欺诈审核';
      if (key === 'occupation' || key === 'occupationOther' || key === 'companyName' || key === 'workYears') return '身份与资质验证';
      if (key === 'monthlyLivingExpense' || key === 'expenseTransferRecords') return '身份与资质验证';
      if (key === 'monthlyIncome') return '收入能力评估';
      if (key === 'incomeProofDocs') return occupation === AUDIT_OCCUPATION_STUDENT ? '身份与资质验证' : '收入能力评估';
      if (key === 'sesameCreditScore' || key === 'wechatPayScore' || key === 'creditReportStatus' || key === 'rentalHistoryStatus') return '信用历史评估';
      if (key === 'status') return '完成审核';
      return '表单';
    };

    const getFieldLabel = (namePath: (string | number)[]) => {
      const root = namePath?.[0];
      if (root === 'auditContacts') {
        const idx = typeof namePath?.[1] === 'number' ? namePath[1] : Number(namePath?.[1]);
        const fieldKey = String(namePath?.[2] ?? '');
        const labelMap: Record<string, string> = {
          contactName: '姓名',
          relationship: '关系',
          phoneNumber: '电话',
          contactProofMaterials: '证明材料',
        };
        const suffix = labelMap[fieldKey] || fieldKey || '字段';
        const n = Number.isFinite(idx) ? idx + 1 : '';
        return `社会关系-联系人${n}${suffix ? `-${suffix}` : ''}`;
      }

      if (root === 'audit') {
        const key = String(namePath?.[1] ?? '');
        const labelMap: Record<string, string> = {
          suspectedCashOutRemark: '套现说明',
          suspectedMachineRemark: '撸机说明',
          suspectedProxyOrderRemark: '代下/刷单说明',
          occupation: '职业',
          occupationOther: '具体职业',
          companyName: '工作单位',
          workYears: '工作年限',
          incomeProofDocs: occupation === AUDIT_OCCUPATION_STUDENT ? '证明材料' : '收入证明材料',
          monthlyLivingExpense: '月均生活费',
          expenseTransferRecords: '生活费转账记录',
          monthlyIncome: '月均收入',
          sesameCreditScore: '芝麻信用分',
          wechatPayScore: '微信支付分',
          creditReportStatus: '征信记录',
          rentalHistoryStatus: '历史租赁记录',
          status: '审核结论',
        };
        return labelMap[key] || key || '字段';
      }

      return '字段';
    };

    const missingItems: Array<{ moduleName: string; label: string }> = errorFields
      .map((f: any) => ({
        namePath: Array.isArray(f?.name) ? (f.name as (string | number)[]) : [],
      }))
      .filter((i: any) => i.namePath.length > 0)
      .map((i: any) => {
        const moduleName = getModuleName(i.namePath);
        const label = getFieldLabel(i.namePath);
        return { moduleName, label };
      });

    const moduleCountMap = new Map<string, number>();
    for (const i of missingItems) {
      moduleCountMap.set(i.moduleName, (moduleCountMap.get(i.moduleName) || 0) + 1);
    }
    const modules = Array.from(moduleCountMap.entries()).sort((a, b) => b[1] - a[1]).map((i) => i[0]);

    const labels = Array.from(new Set(missingItems.map((i) => i.label))) as string[];
    return { count: missingItems.length, labels, modules };
  };

  const initPage = async () => {
    if (!orderNo) return;
    setLoading(true);
    try {
      const [auditResp, orderDetailResp] = await Promise.all([
        orderAuditApi.getAudit(orderNo),
        orderApi.getDetail(orderNo).catch(() => null),
      ]);

      if (auditResp.code !== 200) {
        throw new Error(auditResp.message || '加载失败');
      }

      const mergedOrder: MergedOrder = {
        ...auditResp.data,
        order: auditResp.data.order || orderDetailResp?.order,
        orderData: auditResp.data.orderData || orderDetailResp?.orderData,
        orderRecord: auditResp.data.orderRecord || orderDetailResp?.orderRecord,
        preRating: auditResp.data.preRating || orderDetailResp?.preRating,
        buerReport: auditResp.data.buerReport || orderDetailResp?.buerReport,
      };

      setOrderDetail(mergedOrder);

      // Get audit config from backend response
      if (auditResp.data?.auditConfig) {
        setAuditConfig(auditResp.data.auditConfig as any);
      }

      // Initialize form with structured data
      const auditData = normalizeAuditEntityForForm(mergedOrder.audit || {});

      let contacts = mergedOrder.auditContacts || [];
      if (contacts.length === 0) {
        contacts = [{}, {}] as OrderAuditContactEntity[];
      } else {
        // Sort by sortOrder if available
        contacts.sort((a: OrderAuditContactEntity, b: OrderAuditContactEntity) => (a.sortOrder || 0) - (b.sortOrder || 0));
      }
      while (contacts.length < 2) {
        contacts.push({} as OrderAuditContactEntity);
      }

      form.setFieldsValue({
        audit: auditData,
        auditContacts: contacts,
        auditOther: {},
        teamExtraAuditInfo: auditData.teamExtraAuditInfo || {},
      });

    } catch (error) {
      console.error(error);
      message.error('页面初始化失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const getRule = (fieldName: string, defaultRequired: boolean = false, message?: string) => {
    const config = auditConfig.default.find(c => c.fieldName === fieldName);
    const isRequired = config ? config.isRequired : defaultRequired;
    return isRequired ? [{ required: true, message: message || `请填写/选择该项` }] : [];
  };

  const [suspendModalVisible, setSuspendModalVisible] = useState(false);
  const [interruptModalVisible, setInterruptModalVisible] = useState(false);

  /**
   * 打开暂存弹窗
   */
  const handleSuspendClick = () => {
    setSuspendModalVisible(true);
  };

  /**
   * 确认暂存待办（强制输入原因）
   */
  const handleSuspendConfirm = async (reasonInput: string) => {
    if (!orderNo) return;
    const reason = reasonInput.trim();
    if (!reason) {
      message.warning('请输入暂存原因');
      return;
    }

    setSubmitting(true);
    try {
      const values = form.getFieldsValue(true) as AuditParams;

      // Re-sort contacts before submit to ensure sortOrder is correct
      const contacts = values.auditContacts?.map((c, idx) => ({ ...c, sortOrder: idx + 1 })) || [];

      const payload = {
        audit: {
          ...buildAuditEntityForSubmit(values.audit || {}),
          teamExtraAuditInfo: values.teamExtraAuditInfo,
        },
        auditContacts: contacts,
        auditOther: {
          ...(values.auditOther || {}),
          auditStartTime: isoStartTime,
          auditEndTime: dayjs().toISOString(),
          suspendReason: reason,
        }
      };

      const resp = await orderAuditApi.changeStatus(orderNo, OperationAction.Suspend, payload);
      if (resp.code !== 200) throw new Error(resp.message || '暂存失败');

      message.success('暂存成功');
      setSuspendModalVisible(false);
      setOrderDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          order: {
            ...prev.order,
            auditStatus: 2,
          },
          audit: {
            ...(prev.audit || {}),
            status: 2,
          },
        };
      });
      await initPage();
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '暂存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResume = async () => {
    if (!orderNo) return;
    setSubmitting(true);
    try {
      const resp = await orderAuditApi.changeStatus(orderNo, OperationAction.ResumeAudit);
      if (resp.code !== 200) throw new Error(resp.message || '继续审核失败');

      const now = dayjs();
      setStartTime(now.format('YYYY-MM-DD HH:mm:ss'));
      setIsoStartTime(now.toISOString());
      message.success('已继续审核');
      setOrderDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          order: {
            ...prev.order,
            auditStatus: 1,
          },
          audit: {
            ...(prev.audit || {}),
            status: 1,
          },
        };
      });
      await initPage();
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '继续审核失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenFinishModal = async () => {
    try {
      const values = form.getFieldsValue(true);
      const sesame = values?.audit?.sesameCreditScore;
      const wechat = values?.audit?.wechatPayScore;
      if ((sesame === undefined || sesame === null) && (wechat === undefined || wechat === null)) {
        Modal.warning({
          title: '请完善表单信息后再提交',
          content: '芝麻信用分 和 微信支付分 至少需选填其一',
        });
        return;
      }
      
      await form.validateFields();
      setFinishModalVisible(true);
    } catch (error: any) {
      const summary = buildValidationSummary(error);
      if (summary.count > 0 && summary.count <= 8) {
        Modal.warning({
          title: '请完善表单信息后再提交',
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>缺失字段：</div>
              <div>
                {summary.labels.map((label) => (
                  <div key={label}>- {label}</div>
                ))}
              </div>
              {summary.modules.length > 0 ? (
                <div style={{ marginTop: 12, color: 'rgba(0,0,0,0.65)' }}>涉及模块：{summary.modules.join('、')}</div>
              ) : null}
            </div>
          ),
        });
      } else if (summary.count > 0) {
        message.error(`请完善表单信息后再提交（缺失 ${summary.count} 项，涉及：${summary.modules.join('、')}）`);
      } else {
        message.error('请完善表单信息后再提交');
      }
    }
  };

  const handleFinishSubmit = async () => {
    if (!orderNo) return;

    // Check status in audit object
    const status = form.getFieldValue(['audit', 'status']);
    if (!status) {
      message.error('请选择审核结果');
      return;
    }

    setSubmitting(true);
    try {
      const values = form.getFieldsValue();
      const contacts = values.auditContacts?.map((c, idx) => ({ ...c, sortOrder: idx + 1 })) || [];

      const payload = {
        audit: {
          ...buildAuditEntityForSubmit(values.audit),
          teamExtraAuditInfo: values.teamExtraAuditInfo,
        },
        auditContacts: contacts,
        auditOther: {
          ...(values.auditOther || {}),
          auditStartTime: isoStartTime,
          auditEndTime: dayjs().toISOString()
        }
      };

      const resp = await orderAuditApi.changeStatus(orderNo, OperationAction.SubmitAudit, payload);
      if (resp.code !== 200) throw new Error(resp.message || '提交失败');

      message.success('提交成功');
      setFinishModalVisible(false);
      navigate('/order/audit/done');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const [profitModalVisible, setProfitModalVisible] = useState(false);
  const [decisionModalVisible, setDecisionModalVisible] = useState(false);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;

  const auditOwnerId = orderDetail?.order?.auditId || 0;
  const auditStatusRaw = orderDetail?.audit?.status;
  const isEditableAuditStatus = auditStatusRaw === 1;
  const isSuspendStatus = auditStatusRaw === 2;
  const isCurrentUserOwner = currentUserId > 0 && auditOwnerId > 0 && auditOwnerId === currentUserId;
  const isReauditMode = new URLSearchParams(location.search).get('scene') === 'reaudit';
  const isReadOnlyMode = isViewMode || isReauditMode || !isCurrentUserOwner || !isEditableAuditStatus;
  const canShowAuditAction = !isViewMode && !isReauditMode && isCurrentUserOwner && (isEditableAuditStatus || isSuspendStatus);
  const cannotRenderReason =
    !orderNo || !orderDetail
      ? '无法加载审核数据'
      : auditOwnerId === 0
        ? '该订单未开启审核'
        : '';
  const canRenderForm = !cannotRenderReason;

  const sectionTitleStyle: React.CSSProperties = {
    borderLeft: '3px solid #1890ff',
    paddingLeft: 10,
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 16,
    marginTop: 0
  };
  const pageContainerStyle: React.CSSProperties = {
    padding: '0 16px 12px',
    marginTop: 0,
    height: 'calc(100vh - 94px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    overflow: 'hidden'
  };
  const orderHeaderStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 101,
    background: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  };
  const formScrollStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'auto',
    paddingTop: 8,
    paddingBottom: 72
  };
  const radioWrapStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: 8,
    columnGap: 12,
    alignItems: 'center'
  };
  const footerActionStyle: React.CSSProperties = {
    padding: '8px 0 12px',
    display: 'flex',
    justifyContent: 'flex-end',
    background: '#fff',
    borderTop: '1px solid #f0f0f0',
    position: 'sticky',
    bottom: 0,
    zIndex: 100
  };
  const formColSpan = 8;
  const inputStyle: React.CSSProperties = { width: '100%', maxWidth: 260 };

  const myStartMs = (() => {
    const ranges = orderDetail?.audit?.durationRange;
    const ms = pickDurationRange(ranges, 'startTime', { operatorId: currentUserId });
    return typeof ms === 'number' ? ms : undefined;
  })();
  const myStartTimeLabel = myStartMs ? dayjs(myStartMs).format('YYYY-MM-DD HH:mm:ss') : startTime;

  return (
    <div style={pageContainerStyle}>
      <div style={orderHeaderStyle}>
        {orderDetail ? (
          <OrderBlockShowSimple
            order={orderDetail}
            headerExtra={<Text type="secondary" style={{ fontSize: 14 }}>当前审核开始时间: {myStartTimeLabel}</Text>}
            renderActions={() => {
              if (isReauditMode) {
                return (
                  <Button type="primary" onClick={() => setProfitModalVisible(true)}>
                    利润测算
                  </Button>
                );
              }
              if (canShowAuditAction) {
                return (
                  <Space>
                    <Button
                      loading={submitting}
                      type={isSuspendStatus ? 'primary' : 'default'}
                      style={
                        isSuspendStatus
                          ? undefined
                          : { background: '#faad14', borderColor: '#faad14', color: '#fff' }
                      }
                      onClick={isSuspendStatus ? handleResume : handleSuspendClick}
                    >
                      {isSuspendStatus ? '继续审核' : '暂存待办'}
                    </Button>
                  </Space>
                );
              }
              return null;
            }}
          />
        ) : (
          <Text type="secondary">订单详情加载中...</Text>
        )}
      </div>

      <div style={formScrollStyle}>
        <style dangerouslySetInnerHTML={{ __html: readOnlyFormStyle }} />
        <Card title={null} bordered={false} bodyStyle={{ padding: 0, background: 'transparent' }}>
          {!canRenderForm ? (
            <div style={{ textAlign: 'center', padding: 40, background: '#fff' }}>
              <Text type="secondary">{cannotRenderReason}</Text>
            </div>
          ) : (
            <Form
              form={form}
              className={isReadOnlyMode ? 'readonly-form' : ''}
              disabled={isReadOnlyMode}
              layout="horizontal"
              labelAlign="right"
              labelWrap
              labelCol={{ flex: '140px' }}
              wrapperCol={{ flex: 1 }}
              style={{ width: '100%' }}
              onValuesChange={(changedValues) => {
                // Handle occupation change logic
                if (changedValues.audit && Object.prototype.hasOwnProperty.call(changedValues.audit, 'occupation')) {
                  const newOccupation = changedValues.audit.occupation as string | undefined;
                  const isStudent = newOccupation === AUDIT_OCCUPATION_STUDENT;
                  const isOther = newOccupation === AUDIT_OCCUPATION_OTHER;
                  const audit = form.getFieldValue('audit') || {};

                  form.setFieldsValue({
                    audit: {
                      ...audit,
                      occupationOther: isOther ? audit.occupationOther : undefined,
                      ...(isStudent
                        ? {
                          companyName: undefined,
                          workYears: undefined,
                          monthlyIncome: undefined,
                          incomeProofDocs: [],
                        }
                        : {
                          monthlyLivingExpense: undefined,
                          expenseTransferRecords: [],
                        }),
                    },
                  });
                }
              }}
            >
              {/* Hidden field for startTime removed */}

              {/* 1. Fraud Audit */}
              <Card title="欺诈审核" bordered={false} style={{ marginBottom: 16 }} headStyle={{ fontSize: 18, fontWeight: 'bold' }}>
                <Row gutter={24}>
                  <Col span={8}>
                    <Form.Item name={['audit', 'isCashOutSuspected']} valuePropName="checked">
                      <Checkbox>疑似套现</Checkbox>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(p, c) => p.audit?.isCashOutSuspected !== c.audit?.isCashOutSuspected}>
                      {({ getFieldValue }) => (
                        <Form.Item
                          name={['audit', 'suspectedCashOutRemark']}
                          rules={getFieldValue(['audit', 'isCashOutSuspected']) ? [{ required: true, message: '请输入套现说明' }] : []}
                        >
                          <TextArea rows={3} placeholder="请描述异常情况" count={{ show: true, max: 100 }} />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={['audit', 'isMachineSuspected']} valuePropName="checked">
                      <Checkbox>疑似撸机</Checkbox>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(p, c) => p.audit?.isMachineSuspected !== c.audit?.isMachineSuspected}>
                      {({ getFieldValue }) => (
                        <Form.Item
                          name={['audit', 'suspectedMachineRemark']}
                          rules={getFieldValue(['audit', 'isMachineSuspected']) ? [{ required: true, message: '请输入撸机说明' }] : []}
                        >
                          <TextArea rows={3} placeholder="请描述异常情况" count={{ show: true, max: 100 }} />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={['audit', 'isProxyOrderSuspected']} valuePropName="checked">
                      <Checkbox>疑似代下/刷单</Checkbox>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(p, c) => p.audit?.isProxyOrderSuspected !== c.audit?.isProxyOrderSuspected}>
                      {({ getFieldValue }) => (
                        <Form.Item
                          name={['audit', 'suspectedProxyOrderRemark']}
                          rules={getFieldValue(['audit', 'isProxyOrderSuspected']) ? [{ required: true, message: '请输入代下单说明' }] : []}
                        >
                          <TextArea rows={3} placeholder="请描述异常情况" count={{ show: true, max: 100 }} />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* 2. Limit Audit */}
              <Card title="额度审核" bordered={false} headStyle={{ fontSize: 18, fontWeight: 'bold' }}>

                {/* Identity & Qualification */}
                <div style={sectionTitleStyle}>身份与资质验证</div>
                <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                  <Row gutter={24}>
                    <Col span={8}>
                      <Form.Item name={['audit', 'rentPurpose']} label="租机用途" rules={getRule('rentPurpose', true, '请输入租机用途')}>
                        <Input placeholder="请输入租机用途" style={inputStyle} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name={['audit', 'currentPhone']}
                        label="现用手机型号"
                        rules={getRule('currentPhone', true, '请输入现用手机型号')}
                      >
                        <Input placeholder="请输入现用手机型号" style={inputStyle} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={['audit', 'acceptLockedDevice']} label="是否接受带锁设备" rules={getRule('acceptLockedDevice', true, '请选择是否接受带锁设备')}>
                        <Select placeholder="请选择" style={inputStyle}>
                          <Select.Option value={1}>是</Select.Option>
                          <Select.Option value={0}>否</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item noStyle shouldUpdate={(p, c) => p.audit?.occupation !== c.audit?.occupation}>
                    {({ getFieldValue }) => {
                      const occupation = getFieldValue(['audit', 'occupation']);
                      const isStudent = occupation === AUDIT_OCCUPATION_STUDENT;

                      return (
                        <Row gutter={24}>
                          <Col span={formColSpan}>
                            <Form.Item name={['audit', 'occupation']} label="职业" rules={getRule('occupation', true, '请选择职业')}>
                              <Select placeholder="请选择职业" options={[...AUDIT_OCCUPATION_OPTIONS]} style={inputStyle} />
                            </Form.Item>
                            {occupation === AUDIT_OCCUPATION_OTHER ? (
                              <Form.Item
                                name={['audit', 'occupationOther']}
                                label="具体职业"
                                rules={getRule('occupationOther', true, '请输入具体职业')}
                              >
                                <Input placeholder="请输入具体职业" style={inputStyle} />
                              </Form.Item>
                            ) : null}
                            {isStudent ? (
                              <Form.Item
                                name={['audit', 'monthlyLivingExpense']}
                                label="月均生活费 (元)"
                                rules={getRule('monthlyLivingExpense', true, '请输入月均生活费')}
                              >
                                <InputNumber style={inputStyle} placeholder="请输入月均生活费" />
                              </Form.Item>
                            ) : null}
                          </Col>
                          {isStudent ? (
                            <>
                              <Col span={formColSpan}>
                                <Form.Item
                                  name={['audit', 'incomeProofDocs']}
                                  label="学信网截图/学生证/其他证明材料"
                                  rules={getRule('incomeProofDocs', true, '请上传证明材料')}
                                >
                                  <ResourceUploader
                                    dir={`order_audit/${orderNo}/studentProof`}
                                    maxCount={9}
                                    disabled={isReadOnlyMode}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={formColSpan}>
                                <Form.Item
                                  name={['audit', 'expenseTransferRecords']}
                                  label="生活费转账记录"
                                  rules={getRule('expenseTransferRecords', true, '请上传生活费转账记录')}
                                >
                                  <ResourceUploader
                                    dir={`order_audit/${orderNo}/expenseTransferRecords`}
                                    maxCount={9}
                                    disabled={isReadOnlyMode}
                                  />
                                </Form.Item>
                              </Col>
                            </>
                          ) : (
                            <>
                              <Col span={formColSpan}>
                                <Form.Item noStyle shouldUpdate>
                                  {() => {
                                  const required = getRule('companyName', false, '请输入工作单位').length > 0;
                                  return (
                                    <Form.Item name={['audit', 'companyName']} label="工作单位" rules={getRule('companyName', required, '请输入工作单位')}>
                                      <Input placeholder="请输入工作单位名称" style={inputStyle} />
                                    </Form.Item>
                                  );
                                }}
                                </Form.Item>
                              </Col>
                              <Col span={formColSpan}>
                                <Form.Item noStyle shouldUpdate>
                                {() => {
                                  const required = getRule('workYears', false, '请输入工作年限').length > 0;
                                  return (
                                    <Form.Item name={['audit', 'workYears']} label="工作年限" rules={getRule('workYears', required, '请输入工作年限')}>
                                      <Input placeholder="请输入当前工作年限" style={inputStyle} />
                                    </Form.Item>
                                  );
                                }}
                                </Form.Item>
                              </Col>
                            </>
                          )}
                        </Row>
                      );
                    }}
                  </Form.Item>
                  <Row gutter={24}>
                    <Col span={8}>
                      <Form.Item name={['audit', 'orderAndIdentityCheck']} label="订单及个人身份信息核对" rules={getRule('orderAndIdentityCheck', true, '请选择信息核对情况')}>
                        <Select placeholder="请选择" style={inputStyle}>
                          <Select.Option value={1}>清楚</Select.Option>
                          <Select.Option value={0}>不清楚</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item noStyle shouldUpdate>
                        {() => {
                          const required = getRule('locationAndVerification', false, '请选择定位和核验情况').length > 0;
                          return (
                            <Form.Item name={['audit', 'locationAndVerification']} label="定位和核验" rules={getRule('locationAndVerification', required, '请选择定位和核验情况')}>
                              <Select placeholder="请选择" allowClear style={inputStyle}>
                                <Select.Option value={1}>一致</Select.Option>
                                <Select.Option value={0}>不一致</Select.Option>
                              </Select>
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item noStyle shouldUpdate>
                        {() => {
                          const required = getRule('videoEnvCheck', false, '请选择视频环境核对情况').length > 0;
                          return (
                            <Form.Item name={['audit', 'videoEnvCheck']} label="视频环境核对" rules={getRule('videoEnvCheck', required, '请选择视频环境核对情况')}>
                              <Select placeholder="请选择" allowClear style={inputStyle}>
                                <Select.Option value={1}>正常工作环境</Select.Option>
                                <Select.Option value={2}>正常居住环境</Select.Option>
                                <Select.Option value={3}>正常户外环境</Select.Option>
                                <Select.Option value={4}>异常环境</Select.Option>
                                <Select.Option value={5}>不接受视频</Select.Option>
                              </Select>
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Income Assessment - Hidden for Student */}
                <Form.Item noStyle shouldUpdate={(p, c) => p.audit?.occupation !== c.audit?.occupation}>
                  {({ getFieldValue }) =>
                    getFieldValue(['audit', 'occupation']) !== AUDIT_OCCUPATION_STUDENT ? (
                      <>
                        <div style={sectionTitleStyle}>收入能力评估</div>
                        <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                          <Row gutter={24}>
                            <Col span={formColSpan}>
                              <Form.Item noStyle shouldUpdate>
                                {() => {
                                  const required = getRule('monthlyIncome', false, '请输入月均收入').length > 0;
                                  return (
                                    <Form.Item
                                      name={['audit', 'monthlyIncome']}
                                      label="月均收入 (元)"
                                      rules={getRule('monthlyIncome', required, '请输入月均收入')}
                                    >
                                      <InputNumber style={inputStyle} placeholder="请输入月均收入" />
                                    </Form.Item>
                                  );
                                }}
                              </Form.Item>
                            </Col>
                            <Col span={formColSpan}>
                              <Form.Item
                                name={['audit', 'incomeProofDocs']}
                                label="收入证明材料"
                                rules={getRule('incomeProofDocs', true, '请上传收入证明材料')}
                              >
                                <ResourceUploader
                                  dir={`order_audit/${orderNo}/incomeProofDocs`}
                                  maxCount={9}
                                  disabled={isReadOnlyMode}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      </>
                    ) : null
                  }
                </Form.Item>

                {/* Financial Assessment - Hidden for Student */}
                <Form.Item noStyle shouldUpdate={(p, c) => p.audit?.occupation !== c.audit?.occupation}>
                  {({ getFieldValue }) =>
                    getFieldValue(['audit', 'occupation']) !== AUDIT_OCCUPATION_STUDENT ? (
                      <>
                        <div style={sectionTitleStyle}>家庭财务支出评估</div>
                        <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                          <Row gutter={24}>
                            <Col span={formColSpan}>
                              <Form.Item name={['audit', 'maritalStatus']} label="婚姻状况" rules={getRule('maritalStatus', false, '请选择婚姻状况')}>
                                <Radio.Group>
                                  <Radio value={1}>未婚</Radio>
                                  <Radio value={2}>已婚</Radio>
                                  <Radio value={3}>离异</Radio>
                                  <Radio value={4}>丧偶</Radio>
                                </Radio.Group>
                              </Form.Item>
                            </Col>
                            <Col span={formColSpan}>
                              <Form.Item name={['audit', 'hasChildren']} label="是否有子女" rules={getRule('hasChildren', false, '请选择是否有子女')}>
                                <Radio.Group>
                                  <Radio value={true}>是</Radio>
                                  <Radio value={false}>否</Radio>
                                </Radio.Group>
                              </Form.Item>
                            </Col>
                            <Col span={formColSpan}>
                              <Form.Item name={['audit', 'childrenCount']} label="子女数量" rules={getRule('childrenCount', false, '请输入子女数量')}>
                                <InputNumber placeholder="请输入子女数量" style={inputStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={formColSpan}>
                              <Form.Item name={['audit', 'supportElderly']} label="需抚养老人" rules={getRule('supportElderly', false, '请选择是否需抚养老人')}>
                                <Radio.Group>
                                  <Radio value={true}>是</Radio>
                                  <Radio value={false}>否</Radio>
                                </Radio.Group>
                              </Form.Item>
                            </Col>
                            <Col span={formColSpan}>
                              <Form.Item name={['audit', 'elderlyCount']} label="抚养老人数量" rules={getRule('elderlyCount', false, '请输入抚养老人数量')}>
                                <InputNumber placeholder="请输入抚养老人数量" style={inputStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={formColSpan}>
                              <Form.Item name={['audit', 'otherDebtAmount']} label="其他贷款总额" rules={getRule('otherDebtAmount', false, '请输入其他贷款总额')}>
                                <InputNumber style={inputStyle} placeholder="请输入其他贷款总额" />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      </>
                    ) : null
                  }
                </Form.Item>

                {/* Credit History */}
                <div style={sectionTitleStyle}>信用历史评估</div>
                <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                  <Row gutter={24}>
                    <Col span={formColSpan}>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue, setFieldsValue }) => {
                          const sesame = getFieldValue(['audit', 'sesameCreditScore']);
                          const wechat = getFieldValue(['audit', 'wechatPayScore']);
                          const enableSesame = sesame !== undefined && sesame !== null;
                          const enableWechat = wechat !== undefined && wechat !== null;
                          const requireAtLeastOne = !enableSesame && !enableWechat;

                          return (
                            <div style={{ marginBottom: 24 }}>
                              <div style={{ marginBottom: 8 }}>
                                <Checkbox
                                  checked={enableSesame}
                                  onChange={(e) => {
                                    if (!e.target.checked) setFieldsValue({ audit: { sesameCreditScore: undefined } });
                                    else setFieldsValue({ audit: { sesameCreditScore: '' } }); // trigger render
                                  }}
                                >
                                  芝麻信用分
                                </Checkbox>
                                <Checkbox
                                  checked={enableWechat}
                                  onChange={(e) => {
                                    if (!e.target.checked) setFieldsValue({ audit: { wechatPayScore: undefined } });
                                    else setFieldsValue({ audit: { wechatPayScore: '' } }); // trigger render
                                  }}
                                >
                                  微信支付分
                                </Checkbox>
                                {requireAtLeastOne && (
                                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                                    二者至少需选填其一
                                  </div>
                                )}
                              </div>
                              
                              {enableSesame && (
                                <Form.Item
                                  name={['audit', 'sesameCreditScore']}
                                  label="芝麻信用分"
                                  rules={[
                                    ...getRule('sesameCreditScore', true, '请输入芝麻信用分'),
                                    { type: 'number', min: 550, max: 950, message: '芝麻信用分必须在 550-950 之间' }
                                  ]}
                                >
                                  <InputNumber style={inputStyle} placeholder="请输入芝麻信用分 (建议: 550-950)" />
                                </Form.Item>
                              )}
                              {enableWechat && (
                                <Form.Item
                                  name={['audit', 'wechatPayScore']}
                                  label="微信支付分"
                                  rules={[
                                    ...getRule('wechatPayScore', true, '请输入微信支付分'),
                                    { type: 'number', min: 300, max: 750, message: '微信支付分必须在 300-750 之间' }
                                  ]}
                                >
                                  <InputNumber style={inputStyle} placeholder="请输入微信支付分 (建议: 300-750)" />
                                </Form.Item>
                              )}
                            </div>
                          );
                        }}
                      </Form.Item>
                    </Col>
                    <Col span={formColSpan}>
                      <Form.Item name={['audit', 'creditReportStatus']} label="征信记录" rules={getRule('creditReportStatus', true, '请选择征信记录')}>
                        <Radio.Group style={radioWrapStyle}>
                          <Radio value={1}>良好 (无逾期)</Radio>
                          <Radio value={2}>一般 (少量逾期已还清)</Radio>
                          <Radio value={3}>较差 (当前有逾期)</Radio>
                          <Radio value={4}>无信用记录</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                    <Col span={formColSpan}>
                      <Form.Item name={['audit', 'rentalHistoryStatus']} label="历史租赁记录" rules={getRule('rentalHistoryStatus', true, '请选择历史租赁记录')}>
                        <Radio.Group style={radioWrapStyle}>
                          <Radio value={1}>良好 (无逾期)</Radio>
                          <Radio value={2}>一般 (有验证/逾期)</Radio>
                          <Radio value={3}>较差 (多次逾期)</Radio>
                          <Radio value={4}>无租赁记录</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item name={['audit', 'wechatAlipayScreenRecord']} label="微信/支付宝录屏" rules={getRule('wechatAlipayScreenRecord', true, '请上传微信/支付宝录屏')}>
                        <ResourceUploader maxCount={5} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Team Extra Audit Info */}
                {auditConfig.add && auditConfig.add.length > 0 && (
                  <>
                    <div style={sectionTitleStyle}>团队审核项</div>
                    <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                      <Row gutter={24}>
                        {auditConfig.add.map((field) => (
                          <Col span={formColSpan} key={field.fieldName}>
                            <Form.Item
                              name={['teamExtraAuditInfo', field.displayName || field.fieldName]}
                              label={field.displayName || field.fieldName}
                              rules={[{ required: field.isRequired, message: `请填写/选择${field.displayName || field.fieldName}` }]}
                            >
                              {field.fieldType === 'text' && <Input placeholder={`请输入${field.displayName || field.fieldName}`} style={inputStyle} />}
                              {field.fieldType === 'number' && <InputNumber placeholder={`请输入${field.displayName || field.fieldName}`} style={inputStyle} />}
                              {field.fieldType === 'select' && (
                                <Select placeholder="请选择" style={inputStyle}>
                                  {field.fieldMeta?.options?.map((opt: string) => (
                                    <Select.Option key={opt} value={opt}>{opt}</Select.Option>
                                  ))}
                                </Select>
                              )}
                              {field.fieldType === 'radio' && (
                                <Radio.Group style={radioWrapStyle}>
                                  {field.fieldMeta?.options?.map((opt: string) => (
                                    <Radio key={opt} value={opt}>{opt}</Radio>
                                  ))}
                                </Radio.Group>
                              )}
                              {field.fieldType === 'upload' && (
                                <ResourceUploader
                                  dir={`order_audit/${orderNo}/team_extra/${field.displayName || field.fieldName}`}
                                  maxCount={5}
                                  disabled={isReadOnlyMode}
                                />
                              )}
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </>
                )}

                {/* Social Relations */}
                <div style={sectionTitleStyle}>社会关系</div>
                <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                  <Form.List name="auditContacts">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }, idx) => (
                          <div key={key} style={{ marginBottom: 16, padding: '12px', border: '1px dashed #d9d9d9', borderRadius: '4px' }}>
                            {!isReadOnlyMode && idx > 1 && (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                >
                                  删除
                                </Button>
                              </div>
                            )}
                            <Row gutter={24}>
                              <Col span={formColSpan}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'contactName']}
                                  label={`紧急联系人${idx + 1}姓名`}
                                  rules={[{ required: true, message: '请输入联系人姓名' }]}
                                >
                                  <Input placeholder="请输入联系人姓名" style={inputStyle} />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'phoneNumber']}
                                  label={`联系人${idx + 1}电话`}
                                  rules={[
                                    { required: true, message: '请输入联系人电话' },
                                    {
                                      validator: async (_, value) => {
                                        const raw = typeof value === 'string' ? value.trim() : String(value ?? '').trim();
                                        if (!raw) return;
                                        const normalized = normalizeChinaMobilePhone(raw);
                                        if (!normalized || !isValidChinaMobilePhone(normalized)) {
                                          throw new Error('请输入正确的手机号');
                                        }
                                      },
                                    },
                                  ]}
                                >
                                  <Input placeholder="请输入联系人电话" maxLength={20} style={inputStyle} />
                                </Form.Item>
                              </Col>
                              <Col span={formColSpan}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'relationship']}
                                  label={`联系人${idx + 1}关系`}
                                  rules={[{ required: true, message: '请选择联系人关系' }]}
                                >
                                  <Radio.Group style={radioWrapStyle}>
                                    <Radio value="父母">父母</Radio>
                                    <Radio value="配偶">配偶</Radio>
                                    <Radio value="子女">子女</Radio>
                                    <Radio value="兄弟姐妹">兄弟姐妹</Radio>
                                    <Radio value="朋友">朋友</Radio>
                                    <Radio value="同学/同事">同学/同事</Radio>
                                    <Radio value="其他">
                                      其他:
                                      <Input
                                        style={{ width: 120, marginLeft: 8 }}
                                        placeholder="请输入具体关系"
                                        size="small"
                                        disabled={isReadOnlyMode}
                                      />
                                    </Radio>
                                  </Radio.Group>
                                </Form.Item>
                              </Col>
                              <Col span={formColSpan}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'contactProofMaterials']}
                                  label="联系人证明材料"
                                  rules={[{ required: true, message: '请上传联系人证明材料' }]}
                                >
                                  <ResourceUploader
                                    dir={`order_audit/${orderNo}/contact_${key}`}
                                    maxCount={5}
                                    disabled={isReadOnlyMode}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </div>
                        ))}
                        {isReadOnlyMode ? null : (
                          <Form.Item>
                            <Button type="dashed" onClick={() => add({})} block icon={<PlusOutlined />}>
                              添加社会关系
                            </Button>
                          </Form.Item>
                        )}
                      </>
                    )}
                  </Form.List>
                </div>

                {/* Audit Remarks (Bottom of Page) */}
                <div style={sectionTitleStyle}>其他情况说明</div>
                <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                  <Form.Item name={['audit', 'otherRemarks']} label="其他情况说明" rules={getRule('otherRemarks', false, '请输入其他情况说明')}>
                    <TextArea rows={4} placeholder="请输入其他情况说明..." />
                  </Form.Item>

                  {/* Upload Other Proof Materials */}
                  <Form.Item name={['audit', 'uploadOtherProofMaterials']} label="上传其他证明材料" extra="支持上传支付宝录屏、微信录屏等视频文件，最多9个" rules={getRule('uploadOtherProofMaterials', false, '请上传其他证明材料')}>
                    <ResourceUploader
                      dir={`order_audit/${orderNo}/otherProof`}
                      maxCount={9}
                      disabled={isReadOnlyMode}
                    />
                  </Form.Item>
                </div>

                {/* Supplement Materials & Prepay Voucher (View Only) */}
                {(orderDetail?.audit?.supplementMaterials || orderDetail?.audit?.prepayVoucher) && (
                  <>
                    <div style={sectionTitleStyle}>补充材料与预收凭证</div>
                    <div style={{ paddingLeft: 8, marginBottom: 16 }}>
                      {orderDetail?.audit?.supplementMaterials && (
                        <Form.Item label="已补充的材料">
                          {Array.isArray(orderDetail.audit.supplementMaterials) ? (
                            <ResourceUploader value={orderDetail.audit.supplementMaterials} disabled />
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                              {Object.entries(orderDetail.audit.supplementMaterials).map(([name, urls]) => (
                                <div key={name} style={{ background: '#fafafa', padding: 12, borderRadius: 8 }}>
                                  <div style={{ fontWeight: 500, marginBottom: 8 }}>{name}</div>
                                  <ResourceUploader value={urls as string[]} disabled />
                                </div>
                              ))}
                            </div>
                          )}
                        </Form.Item>
                      )}
                      {orderDetail?.audit?.prepayVoucher && (
                        <Form.Item label="已上传的预收凭证">
                          <ResourceUploader value={orderDetail.audit.prepayVoucher} disabled />
                        </Form.Item>
                      )}
                    </div>
                  </>
                )}

              </Card>
            </Form>
          )}
        </Card>
      </div>

      {isReadOnlyMode ? null : (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px 24px',
          background: '#fff',
          borderTop: '1px solid #e8e8e8',
          textAlign: 'right',
          zIndex: 100
        }}>
          <Space>
            <Button onClick={() => setInterruptModalVisible(true)}>审核中断</Button>
            <Button type="primary" loading={submitting} onClick={handleOpenFinishModal}>完成审核</Button>
          </Space>
        </div>
      )}

      {/* Finish Audit Modal */}
      {isReadOnlyMode ? null : (
        <Modal
          title="完成审核"
          open={finishModalVisible}
          onCancel={() => setFinishModalVisible(false)}
          onOk={handleFinishSubmit}
          confirmLoading={submitting}
          okText="提交"
          cancelText="取消"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name={['audit', 'status']}
              label="审核结论"
              rules={[{ required: true, message: '请选择审核结论' }]}
            >
              <Radio.Group>
                <Radio value={3}>通过</Radio>
                <Radio value={4}>拒绝</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name={['audit', 'otherRemarks']}
              label="其他情况说明"
            >
              <TextArea rows={4} placeholder="请输入其他情况说明..." />
            </Form.Item>
          </Form>
        </Modal>
      )}


      {/* 暂存待办原因弹窗 */}
      <SuspendPendingModal
        open={suspendModalVisible}
        onCancel={() => setSuspendModalVisible(false)}
        onConfirm={handleSuspendConfirm}
        loading={submitting}
      />

      {/* 审核中断弹窗 */}
      {orderNo && (
        <AuditInterruptModal
          visible={interruptModalVisible}
          orderNo={orderNo}
          auditPayload={{
            audit: buildAuditEntityForSubmit(form.getFieldValue('audit') || {}),
            auditContacts: form.getFieldValue('auditContacts') || [],
            auditOther: form.getFieldValue('auditOther') || {},
          }}
          onCancel={() => setInterruptModalVisible(false)}
          onSuccess={() => {
            setInterruptModalVisible(false);
            window.close(); // 中断后关闭页面或返回
          }}
        />
      )}

      {orderDetail && (
        <ProfitEstimationModal
          orderNo={orderDetail.order.orderNo}
          visible={profitModalVisible}
          onClose={() => setProfitModalVisible(false)}
          onDecision={() => setDecisionModalVisible(true)}
        />
      )}

      {orderDetail && decisionModalVisible && (
        <DecisionModal
          visible={decisionModalVisible}
          order={orderDetail}
          onCancel={() => setDecisionModalVisible(false)}
          onSuccess={() => {
            setDecisionModalVisible(false);
            setProfitModalVisible(false);
            initPage();
          }}
          source="decision"
        />
      )}
    </div>
  );
};

export default OrderAuditPage;
